import axiosInstance from '../axiosConfig';

export interface Partner {
    id: number;
    name?: string;           // Make optional with ?
    image: string;
    image_url: string;
    website?: string;         // Make optional with ?
    description?: string;     // Make optional with ?
    order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface PartnersResponse {
    success: boolean;
    data: Partner[];
    message: string;
    count: number;
    meta?: {
        total: number;
        per_page: number;
        current_page: number;
        last_page: number;
    };
}

export interface PartnerResponse {
    success: boolean;
    data: Partner;
    message: string;
}

export const partnerService = {
    // Initialize CSRF token
    async initializeCsrf() {
        try {
            await axiosInstance.get('/sanctum/csrf-cookie');
            console.log('CSRF token initialized');
        } catch (error) {
            console.error('Failed to initialize CSRF token:', error);
        }
    },

    // Get all partners with pagination and search
    async getAll(params?: { page?: number; per_page?: number; search?: string }): Promise<PartnersResponse> {
        try {
            const response = await axiosInstance.get('/api/admin/partners', { params });
            return response.data;
        } catch (error) {
            console.error('Error in getAll:', error);
            throw error;
        }
    },

    // Get single partner by ID
    async getById(id: number): Promise<PartnerResponse> {
        try {
            const response = await axiosInstance.get(`/api/admin/partners/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error in getById:', error);
            throw error;
        }
    },

    // Create new partner
    async create(formData: FormData): Promise<PartnerResponse> {
        try {
            const response = await axiosInstance.post('/api/admin/partners', formData);
            return response.data;
        } catch (error) {
            console.error('Error in create:', error);
            throw error;
        }
    },

    // Update partner
    async update(id: number, formData: FormData): Promise<PartnerResponse> {
        try {
            formData.append('_method', 'PUT');
            const response = await axiosInstance.post(`/api/admin/partners/${id}`, formData);
            return response.data;
        } catch (error) {
            console.error('Error in update:', error);
            throw error;
        }
    },

    // Delete partner
    async delete(id: number): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axiosInstance.delete(`/api/admin/partners/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error in delete:', error);
            throw error;
        }
    },

    // Bulk delete partners
    async bulkDelete(ids: number[]): Promise<{ success: boolean; message: string; deleted_count?: number }> {
        try {
            const response = await axiosInstance.post('/api/admin/partners/bulk-delete', { ids });
            return response.data;
        } catch (error) {
            console.error('Error in bulkDelete:', error);
            throw error;
        }
    },

    // Update status
    async updateStatus(id: number, isActive: boolean): Promise<{ success: boolean; message: string; data: any }> {
        try {
            const response = await axiosInstance.patch(`/api/admin/partners/${id}/status`, {
                is_active: isActive
            });
            return response.data;
        } catch (error) {
            console.error('Error in updateStatus:', error);
            throw error;
        }
    },

    // Reorder partners
    async reorder(orders: { id: number; order: number }[]): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axiosInstance.post('/api/admin/partners/reorder', { orders });
            return response.data;
        } catch (error) {
            console.error('Error in reorder:', error);
            throw error;
        }
    }
};